import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ControlBarButton } from '@m12s/component-library';
import { user, signOut } from 'lib/icons';

import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import { getOperatorIsLoggingOut } from 'lib/selectors/getOperatorIsLoggingOut';
import { getIsOperatorTrackingEnabled } from 'lib/selectors/getIsOperatorTrackingEnabled';
import { MobileIcon, ResponsiveText } from './styled';

/**
 * Shared authentication controls component that handles sign-in and sign-out functionality
 * @param {Object} props
 * @param {boolean} [props.showSignIn=true] - Whether to show sign-in button when no operator
 * @param {boolean} [props.showSignOut=true] - Whether to show sign-out button when operator is signed in
 */
const AuthenticationControls = ({ showSignIn = true, showSignOut = true }) => {
  const { t } = useTranslation();
  const operator = useSelector(getCurrentOperator);
  const isLoggingOut = useSelector(getOperatorIsLoggingOut);
  const isOperatorTrackingEnabled = useSelector(getIsOperatorTrackingEnabled);
  const { openSelectOperator } = useSelectOperator();
  const requestOperatorLogout = useOperatorLogout();

  if (!isOperatorTrackingEnabled) {
    return null;
  }

  // If no operator is signed in, show sign-in button
  if (!operator && showSignIn) {
    return (
      <ControlBarButton
        id="sign-in"
        disabled={isLoggingOut}
        onClick={() => {
          openSelectOperator();
        }}
      >
        <MobileIcon icon={user} />
        <ResponsiveText
          full={t('signIn|full')}
          medium={t('signIn|medium')}
          short={t('signIn|short')}
        />
      </ControlBarButton>
    );
  }

  // If operator is signed in, show sign-out button
  if (operator && showSignOut) {
    return (
      <ControlBarButton
        id="signout"
        disabled={isLoggingOut}
        onClick={() => {
          requestOperatorLogout();
        }}
      >
        <MobileIcon icon={signOut} />
        <ResponsiveText
          full={t('signout|full')}
          medium={t('signout|medium')}
          short={t('signout|short')}
        />
      </ControlBarButton>
    );
  }

  // Return null if conditions don't match
  return null;
};

AuthenticationControls.propTypes = {
  showSignIn: PropTypes.bool,
  showSignOut: PropTypes.bool,
};

export { AuthenticationControls };
